import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Briefcase, MapPin, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

// Student-facing: TMC openings that match their discipline, with a one-click "I'm interested".
export function JobOpportunities({ jobs, onChanged }) {
  const [openId, setOpenId] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (!jobs) return null;

  const raise = async (job) => {
    setBusy(true);
    try {
      await api.post(`/job-openings/${job.id}/interest`, { note });
      toast.success("Your interest is on its way to our recruiting team");
      setOpenId(null); setNote(""); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const withdraw = async (job) => {
    try {
      await api.delete(`/job-openings/${job.id}/interest`);
      toast.success("Withdrawn"); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card title="Job Opportunities with TMC" data-testid="job-opportunities">
      <p className="text-sm text-zinc-500 mb-4">
        Finishing your rotation? Put your hand up and our recruiting team will reach out — no formal
        application needed.
      </p>
      {jobs.length === 0 ? <EmptyState text="No openings posted for your discipline right now — check back soon." /> : (
        <div className="grid md:grid-cols-2 gap-3">
          {jobs.map((j) => (
            <div key={j.id} className={`border rounded-lg p-4 ${j.interested ? "border-cc-green bg-green-50/40" : "bg-white"}`} data-testid={`job-card-${j.id}`}>
              <div className="flex items-start gap-2.5">
                <span className="w-8 h-8 rounded-md bg-cc-blue/10 flex items-center justify-center shrink-0">
                  <Briefcase className="w-4 h-4 text-cc-blue" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-zinc-900">{j.title}</p>
                  <p className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3 h-3" />
                    {[j.facility_name, j.city, j.state].filter(Boolean).join(" · ") || "Multiple locations"}
                    <span className="ml-1 px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">{j.employment_type}</span>
                  </p>
                </div>
              </div>
              {j.description && <p className="text-xs text-zinc-600 mt-2.5 leading-relaxed">{j.description}</p>}

              {openId === j.id ? (
                <div className="mt-3 space-y-2">
                  <Textarea rows={2} placeholder="Anything you'd like them to know? (optional)"
                    value={note} onChange={(e) => setNote(e.target.value)} data-testid={`job-note-${j.id}`} />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => raise(j)} disabled={busy} className="bg-cc-green hover:bg-green-600 text-white" data-testid={`job-send-${j.id}`}>
                      {busy ? "Sending…" : "Send my interest"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setOpenId(null); setNote(""); }} data-testid={`job-cancel-${j.id}`}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  {j.interested ? (
                    <>
                      <span className="text-xs font-medium text-cc-green flex items-center gap-1" data-testid={`job-interested-${j.id}`}>
                        <CheckCircle2 className="w-4 h-4" />Interest sent — we'll be in touch
                      </span>
                      <button onClick={() => withdraw(j)} className="text-xs text-zinc-400 hover:text-red-600" data-testid={`job-withdraw-${j.id}`}>Withdraw</button>
                    </>
                  ) : (
                    <Button size="sm" onClick={() => { setOpenId(j.id); setNote(""); }} className="bg-cc-blue hover:bg-cc-blue-dark text-white" data-testid={`job-interest-${j.id}`}>
                      I'm interested
                    </Button>
                  )}
                  {j.apply_url && (
                    <a href={j.apply_url} target="_blank" rel="noreferrer" className="text-xs text-cc-blue hover:underline flex items-center gap-1" data-testid={`job-apply-${j.id}`}>
                      Apply online <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
